import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  Image,
  VStack,
  HStack,
  Flex,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  IconButton,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  FormControl,
  FormLabel,
  Checkbox,
  useDisclosure,
  Select,
  Spinner,
} from "@chakra-ui/react";
import { MinusIcon } from "@chakra-ui/icons";
import { supabase } from "../../lib/supabaseClient";
import youtubeApi from "../../lib/youtubeApi";
import { useAuth } from "../../context/AuthContext";
import SendEmail from "../../components/SendEmail";
import { fetchWithToken } from "../../lib/api";

const ChannelManagement = () => {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState(null);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [channelUrl, setChannelUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const cancelRef = useRef();
  const toast = useToast();
  const [hasContract, setHasContract] = useState(null);
  const [contract, setContract] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    channel: "",
    agree: false,
  });
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const isFormValid =
    form.name && form.email && form.phone && form.channel && form.agree;
  const [selectedGenre, setSelectedGenre] = useState("");

  const genres = [
    "영화/드라마",
    "예능/코미디",
    "스포츠",
    "음식/요리",
    "썰/스토리",
    "개인방송/블로그",
    "IT/경제/재테크",
    "게임",
    "의학/과학/교육",
    "뉴스/정치",
    "뷰티",
    "비영리/사회운동",
    "동물",
    "엔터테인먼트",
    "여행/이벤트",
    "애니메이션",
    "음악/댄스",
    "자동차",
    "지식정보/쇼핑",
    "챌린지",
    "키즈",
    "패션",
    "기타",
  ];

  // 채널 데이터 가져오기
  useEffect(() => {
    fetchChannels();
  }, [user]);

  useEffect(() => {
    const checkContract = async () => {
      if (!user?.email) return setHasContract(false);
      const { data, error } = await supabase
        .from("shorts_contract")
        .select("id, partner")
        .eq("contractor", user.email)
        .order("created_date", { ascending: false })
        .maybeSingle();
      if (error) {
        setHasContract(false);
        return;
      }
      setHasContract(!!data);
      setContract(data);
    };
    checkContract();
  }, [user]);

  const fetchChannels = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("shorts_channel")
        .select("*")
        .eq("user", user.email)
        .order("created_date", { ascending: false });

      if (error) throw error;

      setChannels(data || []);
    } catch (error) {
      console.error("Error fetching channels:", error);
      toast({
        title: "채널 데이터를 불러오는데 실패했습니다.",
        status: "error",
        duration: 3000,
      });
    }
  };

  const extractChannelId = async (url) => {
    try {
      const urlObj = new URL(url);
      if (
        urlObj.hostname === "www.youtube.com" ||
        urlObj.hostname === "youtube.com"
      ) {
        // 채널 URL 형식에 따라 ID 추출
        if (urlObj.pathname.startsWith("/channel/")) {
          return urlObj.pathname.split("/")[2];
        } else if (
          urlObj.pathname.startsWith("/c/") ||
          urlObj.pathname.startsWith("/@")
        ) {
          // @핸들이나 /c/로 시작하는 경우 검색 API를 사용하여 실제 채널 ID를 가져옴
          const searchResponse = await youtubeApi.get("/search", {
            params: {
              part: "snippet",
              type: "channel",
              q: urlObj.pathname.split("/")[1],
              maxResults: 1,
            },
          });

          if (!searchResponse.data.items?.length) {
            throw new Error("채널을 찾을 수 없습니다.");
          }

          return searchResponse.data.items[0].id.channelId;
        }
      }
      throw new Error("유효하지 않은 YouTube 채널 URL입니다.");
    } catch (error) {
      throw new Error("유효하지 않은 URL 형식입니다.");
    }
  };

  const getChannelData = async (channelId) => {
    try {
      // 채널 상세 정보 가져오기
      const response = await youtubeApi.get("/channels", {
        params: {
          part: "snippet,statistics",
          id: channelId,
        },
      });

      if (!response.data.items?.length) {
        throw new Error("채널 정보를 찾을 수 없습니다.");
      }

      const channel = response.data.items[0];
      return {
        title: channel.snippet.title,
        thumbnails: channel.snippet.thumbnails.default.url,
        subscriberCount: parseInt(channel.statistics.subscriberCount),
        viewCount: parseInt(channel.statistics.viewCount),
        videoCount: parseInt(channel.statistics.videoCount),
      };
    } catch (error) {
      console.error("YouTube API Error:", error);
      throw new Error("채널 정보를 가져오는데 실패했습니다.");
    }
  };

  const handleRegister = async () => {
    if (!channelUrl.trim()) {
      toast({
        title: "채널 URL을 입력해주세요.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (!selectedGenre) {
      toast({
        title: "장르를 선택해주세요.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    setIsLoading(true);
    try {
      const channelId = await extractChannelId(channelUrl);
      const channelData = await getChannelData(channelId);

      // 1. Supabase에 데이터 저장
      const { data: supabaseData, error: supabaseError } = await supabase
        .from("shorts_channel")
        .insert({
          channel_id: channelId,
          channel_title: channelData.title,
          subscriber_count: channelData.subscriberCount,
          thumbnails: channelData.thumbnails,
          view_count: channelData.viewCount,
          video_count: channelData.videoCount,
          channel_url: channelUrl,
          status: "Pending",
          user: user.email,
          genre: selectedGenre,
          partner: contract.partner || null,
          contract: contract.id || null,
        })
        .select()
        .single();

      if (supabaseError) throw supabaseError;

      // // partnerId: contract.partner(이메일)과 일치하는 user 테이블의 id 조회
      // let partnerUserId = null;
      // if (contract?.partner) {
      //   const { data: partnerUser, error: partnerUserError } = await supabase
      //     .from("user")
      //     .select("id")
      //     .eq("email", contract.partner)
      //     .maybeSingle();
      //   if (partnerUser && partnerUser.id) {
      //     partnerUserId = partnerUser.id;
      //   }
      // }

      // // 2. API로 데이터 전송
      // const accessToken = localStorage.getItem("access_token");
      // const apiData = {
      //   id: supabaseData.id,
      //   channelId: channelId,
      //   channelTitle: channelData.title,
      //   channelUrl: channelUrl,
      //   description: null,
      //   userId: user.id,
      //   subscriberCount: channelData.subscriberCount,
      //   videoCount: channelData.videoCount,
      //   viewCount: channelData.viewCount,
      //   partnerId: partnerUserId,
      //   status: "Pending",
      //   thumbnails: channelData.thumbnails,
      //   registerAt: new Date().toISOString(),
      //   creatorId: user.id,
      //   createAt: new Date().toISOString(),
      //   updateAt: new Date().toISOString(),
      // };

      // const response = await fetch("https://plpl.rhoonart.co.kr/channels", {
      //   method: "POST",
      //   headers: {
      //     "Content-Type": "application/json",
      //     Authorization: `Bearer ${accessToken}`,
      //   },
      //   body: JSON.stringify(apiData),
      // });

      // if (!response.ok) {
      //   window.location.href = "/laeeblyforshorts";
      // }

      toast({
        title: "채널이 등록되었습니다.",
        description: "관리자 승인 후 이용하실 수 있습니다.",
        status: "success",
        duration: 3000,
      });

      setChannelUrl("");
      setSelectedGenre("");
      setIsRegisterModalOpen(false);
      fetchChannels();
    } catch (error) {
      console.error("Error registering channel:", error);
      toast({
        title: "채널 등록에 실패했습니다.",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (channel) => {
    setChannelToDelete(channel);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!channelToDelete) return;

    try {
      // 1. 외부 API에서 삭제 (토큰 발급)
      // user 테이블에서 현재 유저 이메일로 id 조회
      const { data: userData } = await supabase
        .from("user")
        .select("id")
        .eq("email", user.email)
        .single();
      const userId = userData?.id;
      const tokenRes = await fetch("https://plpl.rhoonart.co.kr/auth/token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });
      const tokenJson = await tokenRes.json();
      const access_token = tokenJson.data?.access_token;
      // 외부 API 삭제
      await fetch(
        `https://plpl.rhoonart.co.kr/channels/${channelToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        }
      );

      // 2. Supabase에서 삭제
      const { error } = await supabase
        .from("shorts_channel")
        .delete()
        .eq("id", channelToDelete.id);

      if (error) throw error;

      setChannels((prev) => prev.filter((ch) => ch.id !== channelToDelete.id));
      toast({
        title: "채널이 삭제되었습니다.",
        status: "success",
        duration: 1500,
      });
    } catch (error) {
      console.error("Error deleting channel:", error);
      toast({
        title: "채널 삭제에 실패했습니다.",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setChannelToDelete(null);
    }
  };

  // 상태에 따른 색상과 텍스트 반환
  const getStatusInfo = (status) => {
    switch (status) {
      case "Approved":
        return { color: "green", text: "승인" };
      case "Denied":
        return { color: "red", text: "거절" };
      default:
        return { color: "yellow", text: "대기" };
    }
  };

  // 날짜 포맷 함수
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date
      .toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
      .replace(/\. /g, "-")
      .replace(".", "");
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    setIsLoadingModal(true);
    try {
      await SendEmail({
        email: "contact@plpl.kr",
        ccEmails: user?.email ? [user.email] : [],
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      await SendEmail({
        email: "jaeeui.kwon@rhoonart.com",
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      await SendEmail({
        email: "blair.park@laeebly.com",
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      await SendEmail({
        email: "kirby.lee@laeebly.com",
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      toast({
        title: "상담 요청이 접수되었습니다.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setForm({ name: "", email: "", phone: "", channel: "", agree: false });
      onClose();
    } catch (e) {
      toast({
        title: "상담 요청에 실패했습니다.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingModal(false);
    }
  };

  if (hasContract === null) {
    return (
      <Flex minH="60vh" align="center" justify="center" direction="column">
        <Spinner size="xl" color="teal.500" />
      </Flex>
    );
  }
  if (hasContract === false) {
    return (
      <Flex minH="60vh" direction="column" align="center" justify="center">
        <Text fontSize="md" color="gray.700" mb={2}>
          신규 회원은 레이블리와 계약 체결 후 이용이 가능합니다.
        </Text>
        <Button colorScheme="yellow" size="lg" mt={2} onClick={onOpen}>
          계약 상담 신청하기
        </Button>
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay />
          <ModalContent maxW="lg">
            <ModalHeader>레이블리 이용 계약 문의하기</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={4} fontSize="md">
                신규 회원은 개별 계약 후 이용이 가능합니다. 이용 계약 상담을
                신청해주시면, 기재해주신 연락처로 연락드리겠습니다.
                <br />
                (이메일만 기재할 경우, 스팸으로 분류되는 경우가 많습니다. 이용
                계약 문의주시면, 연락처로 연락드립니)
              </Text>
              <FormControl mb={3} isRequired>
                <FormLabel>성함 (필수)</FormLabel>
                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="성함"
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>이메일 (필수)</FormLabel>
                <Input
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="이메일"
                  type="email"
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>연락처 (필수)</FormLabel>
                <Input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="연락처"
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>운영중인 유튜브 채널 (필수)</FormLabel>
                <Input
                  name="channel"
                  value={form.channel}
                  onChange={handleChange}
                  placeholder="유튜브 채널"
                />
              </FormControl>
              <Checkbox
                name="agree"
                isChecked={form.agree}
                onChange={handleChange}
                mb={2}
              >
                (필수) 레이블리 이용 상담을 위한 개인정보 제공에 동의합니다.
              </Checkbox>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="blue"
                onClick={handleSubmit}
                isLoading={isLoadingModal}
                isDisabled={!isFormValid}
                w="100%"
                size="lg"
              >
                상담 요청하기
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Flex>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      {/* 상단 네비게이션 바 */}
      <Flex w="100%" p={4} mb={6} align="center">
        <HStack spacing={4}>
          <Tag
            colorScheme="teal"
            borderRadius="full"
            px={4}
            py={2}
            fontSize="md"
          >
            보유 채널
          </Tag>
          <Text color="gray.600">운영하고 있는 채널을 등록할 수 있어요.</Text>
        </HStack>
        <Button
          colorScheme="teal"
          size="lg"
          boxShadow="md"
          onClick={() => setIsRegisterModalOpen(true)}
          ml="auto"
        >
          채널 등록
        </Button>
      </Flex>

      {/* 테이블 */}
      <Box bg="white" borderRadius="xl" boxShadow="md" p={0}>
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>채널</Th>
              <Th>채널 수치 (구독자/조회수/영상 수)</Th>
              <Th>장르</Th>
              <Th>등록일</Th>
              <Th>상태</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {channels
              .slice()
              .sort((a, b) => {
                // 대기(Pending) 상태 우선
                if (a.status === "Pending" && b.status !== "Pending") return -1;
                if (a.status !== "Pending" && b.status === "Pending") return 1;
                // 같은 그룹 내에서는 created_date 내림차순
                const dateA = a.created_date
                  ? new Date(a.created_date).getTime()
                  : 0;
                const dateB = b.created_date
                  ? new Date(b.created_date).getTime()
                  : 0;
                return dateB - dateA;
              })
              .map((ch) => {
                const statusInfo = getStatusInfo(ch.status);
                return (
                  <Tr key={ch.id}>
                    <Td>
                      <a
                        href={ch.channel_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: "none" }}
                      >
                        <HStack _hover={{ opacity: 0.7 }}>
                          <Image
                            src={ch.thumbnails}
                            alt={ch.channel_title}
                            boxSize="48px"
                            borderRadius="full"
                            bg="white"
                            fallbackSrc="/images/default-album.png"
                          />
                          <Box>
                            <Text fontWeight="bold">{ch.channel_title}</Text>
                          </Box>
                        </HStack>
                      </a>
                    </Td>
                    <Td>
                      {`${ch.subscriber_count?.toLocaleString()}명 / ${ch.view_count?.toLocaleString()}회 / ${ch.video_count?.toLocaleString()}개`}
                    </Td>
                    <Td>
                      <Text fontSize="md" fontWeight="medium">
                        {ch.genre || "-"}
                      </Text>
                    </Td>
                    <Td>{formatDate(ch.created_date)}</Td>
                    <Td>
                      <HStack>
                        <Box
                          w={3}
                          h={3}
                          borderRadius="full"
                          bg={`${statusInfo.color}.400`}
                        />
                        <Text
                          color={`${statusInfo.color}.600`}
                          fontWeight="bold"
                        >
                          {statusInfo.text}
                        </Text>
                      </HStack>
                    </Td>
                    <Td>
                      <IconButton
                        icon={<MinusIcon />}
                        aria-label="채널 삭제"
                        variant="ghost"
                        colorScheme="gray"
                        onClick={() => handleDeleteClick(ch)}
                      />
                    </Td>
                  </Tr>
                );
              })}
          </Tbody>
        </Table>
      </Box>

      {/* 채널 등록 모달 */}
      <Modal
        isOpen={isRegisterModalOpen}
        onClose={() => {
          setIsRegisterModalOpen(false);
          setChannelUrl("");
          setSelectedGenre("");
        }}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <Image
                src="/images/youtube-play.png"
                alt="YouTube Icon"
                boxSize="40px"
              />
              <Text>채널 등록</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text mb={4}>음반을 사용하는 모든 채널을 등록이 필요해요.</Text>
            <Text ms={2} mb={8} color="gray.600" fontSize="sm">
              등록되지 않은 채널에서 발생한 수익 미정산으로 인해 발생하는 문제는
              (주)루나르트에서 도움드릴 수 없어요. 꼭 운영하고 있는 채널을 모두
              등록해주세요.
            </Text>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>유튜브 채널의 주소를 입력하세요</FormLabel>
                <Input
                  placeholder="예) https://www.youtube.com/@channelname"
                  value={channelUrl}
                  onChange={(e) => setChannelUrl(e.target.value)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>채널 장르를 선택하세요</FormLabel>
                <Select
                  placeholder="장르를 선택해주세요"
                  value={selectedGenre}
                  onChange={(e) => setSelectedGenre(e.target.value)}
                >
                  {genres.map((genre) => (
                    <option key={genre} value={genre}>
                      {genre}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
            <Flex justify="flex-end" mt={8}>
              <Button
                colorScheme="teal"
                onClick={handleRegister}
                isLoading={isLoading}
                loadingText="등록 신청하기"
              >
                등록 신청하기
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* 삭제 확인 대화상자 */}
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              채널 삭제 확인
            </AlertDialogHeader>

            <AlertDialogBody>
              {channelToDelete && (
                <>
                  <Text mb={4}>
                    정말 <strong>{channelToDelete.channel_title}</strong> 채널을
                    삭제하시겠습니까?
                  </Text>
                  <Text color="red.500">이 작업은 되돌릴 수 없습니다.</Text>
                </>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => setIsDeleteDialogOpen(false)}
              >
                취소
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                삭제
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
};

export default ChannelManagement;
